<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <!-- Header -->
      <div class="card-header">
        <h2 class="text-xl font-semibold text-gray-900">
          {{ link ? 'Edit Link' : 'Create New Link' }}
        </h2>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="card-body">
        <div class="space-y-4">
          <!-- Tenant ID (only when creating) -->
          <div v-if="!link" class="form-group">
            <label for="tenantId" class="form-label">Tenant *</label>
            <select id="tenantId" v-model="form.tenantId" class="form-input" :class="{ error: errors.tenantId }" required>
              <option value="" disabled>Select tenant</option>
              <option v-for="t in tenants" :key="t.tenantId" :value="t.tenantId">
                {{ t.name }} ({{ t.tenantId }})
              </option>
            </select>
            <div v-if="errors.tenantId" class="form-error">{{ errors.tenantId }}</div>
          </div>

          <!-- Redirect URL -->
          <div class="form-group">
            <label for="redirectURL" class="form-label">Redirect URL *</label>
            <input
              id="redirectURL"
              v-model="form.redirectURL"
              type="url"
              class="form-input"
              :class="{ error: errors.redirectURL }"
              placeholder="https://example.com"
              required
            />
            <div v-if="errors.redirectURL" class="form-error">
              {{ errors.redirectURL }}
            </div>
          </div>

          <!-- Title -->
          <div class="form-group">
            <label for="title" class="form-label">Title</label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              class="form-input"
              placeholder="Optional title for the link"
            />
          </div>

          <!-- Description -->
          <div class="form-group">
            <label for="description" class="form-label">Description</label>
            <textarea
              id="description"
              v-model="form.description"
              class="form-input"
              rows="3"
              placeholder="Optional description"
            ></textarea>
          </div>

          <!-- Active Status (only for editing) -->
          <div v-if="link" class="form-group">
            <label class="form-label">Status</label>
            <div class="flex items-center gap-4">
              <label class="flex items-center">
                <input
                  v-model="form.isActive"
                  type="radio"
                  :value="true"
                  class="mr-2"
                />
                Active
              </label>
              <label class="flex items-center">
                <input
                  v-model="form.isActive"
                  type="radio"
                  :value="false"
                  class="mr-2"
                />
                Inactive
              </label>
            </div>
          </div>

          <!-- Expiration Date -->
          <div class="form-group">
            <label for="expiresAt" class="form-label">Expiration Date</label>
            <input
              id="expiresAt"
              v-model="form.expiresAt"
              type="datetime-local"
              class="form-input"
            />
            <p class="text-sm text-gray-600 mt-1">
              Leave empty for no expiration
            </p>
          </div>
        </div>

        <!-- Error Message -->
        <ErrorMessage 
          v-if="submitError" 
          :message="submitError" 
          title="Error Saving Link"
        />

        <!-- Actions -->
        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            @click="$emit('close')"
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner mr-2"></span>
            {{ isSubmitting ? 'Saving...' : (link ? 'Update Link' : 'Create Link') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue'
 import ErrorMessage from '@/components/common/ErrorMessage.vue'
 import type { Link, CreateLinkRequest, UpdateLinkRequest, AdminCreateLinkRequest, Tenant } from '@/types'
 import { useAdminStore } from '@/stores/admin'
 import { linksService } from '@/services/links'

interface Props {
  link?: Link | null
}

interface Emits {
  (e: 'close'): void
  (e: 'save', data: AdminCreateLinkRequest | UpdateLinkRequest): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const adminStore = useAdminStore()
const tenants = computed<Tenant[]>(() => adminStore.tenants)

// Form state
const form = reactive({
  tenantId: '',
  redirectURL: '',
  title: '',
  description: '',
  isActive: true,
  expiresAt: ''
})

// Validation errors
const errors = reactive({
  tenantId: '',
  redirectURL: ''
})

// Submit state
const isSubmitting = ref(false)
const submitError = ref('')

// Convert a Date or timestamp to a string suitable for input[type="datetime-local"] in local time
const toLocalDateTimeInputValue = (value: unknown): string => {
  if (value === undefined || value === null) return ''
  const date = (typeof value === 'number' || typeof value === 'string')
    ? new Date(Number(value))
    : (value as Date)
  if (isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

// Initialize form when link prop changes
watch(() => props.link, (newLink) => {
  if (newLink) {
    // Edit mode
    form.redirectURL = newLink.redirectURL
    form.title = newLink.title || ''
    form.description = newLink.description || ''
    form.isActive = newLink.isActive
    form.expiresAt = newLink.expiresAt ? toLocalDateTimeInputValue(newLink.expiresAt) : ''
  } else {
    // Create mode
    form.tenantId = ''
    form.redirectURL = ''
    form.title = ''
    form.description = ''
    form.isActive = true
    form.expiresAt = ''
  }
  // Clear errors
  errors.tenantId = ''
  errors.redirectURL = ''
  submitError.value = ''
}, { immediate: true })

// Validation
const validateForm = () => {
  errors.tenantId = ''
  errors.redirectURL = ''

  if (!props.link && !form.tenantId) {
    errors.tenantId = 'Tenant ID is required'
    return false
  }
  
  if (!form.redirectURL) {
    errors.redirectURL = 'Redirect URL is required'
    return false
  }
  
  try {
    new URL(form.redirectURL)
  } catch {
    errors.redirectURL = 'Please enter a valid URL'
    return false
  }
  
  return true
}

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) return
  
  isSubmitting.value = true
  submitError.value = ''
  
  try {
    const data: any = {
      redirectURL: form.redirectURL,
      title: form.title || undefined,
      description: form.description || undefined
    }
    // Include tenantId when creating
    if (!props.link) {
      data.tenantId = form.tenantId
    }
    // Add expiration if set
    if (form.expiresAt) {
      data.expiresAt = new Date(form.expiresAt).toISOString()
    }
    
    // Add active status for editing
    if (props.link) {
      data.isActive = form.isActive
    }
    
    emit('save', data)
  } catch (error: any) {
    submitError.value = error.message || 'Failed to save link'
  } finally {
    isSubmitting.value = false
  }
}

// Fetch tenants when opening for create
onMounted(async () => {
  try {
    if (!props.link && tenants.value.length === 0) {
      await adminStore.fetchTenants()
    }
    // When editing, fetch the latest link to ensure we have expiresAt, etc.
    if (props.link?.shortCode) {
      const fresh = await linksService.getLink(props.link.shortCode)
      form.redirectURL = fresh.redirectURL
      form.title = fresh.title || ''
      form.description = fresh.description || ''
      form.isActive = fresh.isActive
      form.expiresAt = fresh.expiresAt ? toLocalDateTimeInputValue(fresh.expiresAt) : ''
    }
  } catch {}
})
</script>
